import React, { ReactNode } from "react";
import styled from "styled-components";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Button,
  Theme,
  colors,
  font,
  media,
} from "@literati/public-ui-shared-react";
import {
  DialogContent,
  DialogOverlay,
  centerDesktopContentStyles,
} from "components/Dialog/DialogPrimitives";
import MembershipFall25OfferBox from "components/Membership/MembershipFall25OfferBox";

const CLASS_BASE = "special-offer";

export const MembershipDialogFall25CouponClassName = {
  OVERLAY: `${CLASS_BASE}-overlay`,
  POPUP: `${CLASS_BASE}-popup`,
  CLOSE: `${CLASS_BASE}-close`,
  IMAGE: `${CLASS_BASE}-image`,
  HEADING: `${CLASS_BASE}-heading`,
  CLAIM_BUTTON: "claim-offer-btn",
  DISCOUNT_TEXT: "discount-text",
  OFFER_BOX: "three-month-offer-box",
  OFFER_FIRST: "offer-first-container",
  OFFER_SECOND: "offer-second-container",
  OFFER_BADGE: "offer-badge",
  BADGE_TEXT: "badge-txt",
  OFFER_MAIN_HEADING: "offer-main-heading",
  OFFER_SUBTEXT: "offer-subtext",
  PRICE_CONTAINER: "price-container",
  MAIN_PRICE: "main-price",
  COMPARE_PRICE: "compare-price",
  PRICE_TEXT: "price-text",
  OFFER_NOTE: "offer-note",
} as const;

export interface MembershipDialogFall25CouponProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: ReactNode;
  onClaimOffer?: () => void;
  imageSrc?: string;
}

const StyledOverlay = styled(DialogOverlay).attrs({
  className: MembershipDialogFall25CouponClassName.OVERLAY,
})``;

const StyledContent = styled(DialogContent).attrs({
  className: MembershipDialogFall25CouponClassName.POPUP,
})`
  padding: 1.25rem;
  border-radius: 16px;
  max-width: 27rem;
  overflow: hidden;

  ${media.md} {
    ${centerDesktopContentStyles}
  }
`;

const CloseButton = styled(Dialog.Close).attrs({
  className: MembershipDialogFall25CouponClassName.CLOSE,
  "aria-label": "Close special offer",
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 0;
  margin-left: auto;
  background: transparent;
  cursor: pointer;
`;

const StyledImage = styled.img.attrs({
  className: MembershipDialogFall25CouponClassName.IMAGE,
  alt: "Literati book box",
})`
  display: block;
  width: 152px;
  height: 72px;
  margin: 0.25rem auto 0;
  object-fit: contain;
  border-radius: 12px;
`;

const Heading = styled.div.attrs({
  className: MembershipDialogFall25CouponClassName.HEADING,
})`
  font-family: ${font.fontFamily.hoeflerTitling};
  margin-top: 1rem;
  text-align: center;
  color: ${colors.inkBlack};
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
`;

const ClaimOfferButton = styled(Button).attrs({
  className: MembershipDialogFall25CouponClassName.CLAIM_BUTTON,
  type: "button",
  variant: "primary",
})`
  width: 100%;
  max-width: none;
  border-radius: 9999px;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 800;
`;

const DiscountText = styled.div.attrs({
  className: MembershipDialogFall25CouponClassName.DISCOUNT_TEXT,
})`
  margin-top: 0.75rem;
  color: ${colors.inkBlack};
  font-size: 1rem;
  text-align: center;
  padding-bottom: 1rem;
`;

const DEFAULT_IMAGE_SRC =
  "https://siren-images.literati.com/images/lit-sub-promo-card.original.png";

export const MembershipDialogFall25Coupon = ({
  open,
  onOpenChange,
  trigger,
  onClaimOffer,
  imageSrc = DEFAULT_IMAGE_SRC,
}: MembershipDialogFall25CouponProps) => {
  return (
    <Theme>
      <Dialog.Root open={open} onOpenChange={onOpenChange} modal>
        {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
        <Dialog.Portal>
          <StyledOverlay />
          <StyledContent>
            <CloseButton>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M13.7 0.3C13.3 -0.1 12.7 -0.1 12.3 0.3L7 5.6L1.7 0.3C1.3 -0.1 0.7 -0.1 0.3 0.3C-0.1 0.7 -0.1 1.3 0.3 1.7L5.6 7L0.3 12.3C-0.1 12.7 -0.1 13.3 0.3 13.7C0.5 13.9 0.7 14 1 14C1.3 14 1.5 13.9 1.7 13.7L7 8.4L12.3 13.7C12.5 13.9 12.8 14 13 14C13.2 14 13.5 13.9 13.7 13.7C14.1 13.3 14.1 12.7 13.7 12.3L8.4 7L13.7 1.7C14.1 1.3 14.1 0.7 13.7 0.3Z"
                  fill="#232323"
                />
              </svg>
            </CloseButton>
            <StyledImage src={imageSrc} />
            <Heading>Limited Time Offer</Heading>
            <MembershipFall25OfferBox />
            <ClaimOfferButton onClick={onClaimOffer}>
              Claim My Offer
            </ClaimOfferButton>
            <DiscountText>Discount auto-applied at checkout</DiscountText>
          </StyledContent>
        </Dialog.Portal>
      </Dialog.Root>
    </Theme>
  );
};

export default MembershipDialogFall25Coupon;
